import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import Icon from '@mdi/react';
import { mdiTshirtCrew } from '@mdi/js';

function AddTeamModal({ show, handleClose, onTeamAdded }) {
  const [formData, setFormData] = useState({
    name: "",
    league: "",
    captain: "",
    logo: null,
    color: "",
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [colorChoices, setColorChoices] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const fetchLeagues = async () => {
    try {
      const response = await fetch("/api/leagues/leagues/", {
        withCredentials: true,
      });

      if (response.ok) {
        const data = await response.json();
        setLeagues(data); // Populate leagues state
      } else {
        console.error(
          "Failed to fetch leagues:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error fetching leagues:", error);
    } finally {
      setLeaguesLoading(false); // Stop loading spinner
    }
  };

  const fetchSeasons = async (leagueId) => {
    setSeasonsLoading(true);
    try {
      const response = await fetch(`/api/leagues/seasons/?league=${leagueId}`, {
        withCredentials: true,
      });

      if (response.ok) {
        const data = await response.json();
        setSeasons(data); // Populate seasons state
      } else {
        console.error(
          "Failed to fetch seasons:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
    } finally {
      setSeasonsLoading(false);
    }
  };

  const fetchPlayers = async (leagueId) => {
    try {
      const response = await axios.get(
        `/api/player/free_agent/${leagueId}/`,
        {
          withCredentials: true,
        }
      )
      setPlayers(response.data)
    } catch (err) {
      console.error("Error fetching league players:", err)
    }
  }

  useEffect(() => {
    if (!show) return;
    axios
      .get("/api/teams/team-color-choices/")
      .then((response) => {
        setColorChoices(response.data.choices);
      })
      .catch((error) => {
        console.error("Error fetching color choices:", error);
        alert("Failed to fetch team color choices.");
      });
    fetchLeagues();
  }, [show]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "logo" && files && files[0]) {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (files[0].size > maxSize) {
        setErrorMessages(["Logo file size must be less than 5MB"]);
        e.target.value = null; // Clear the file input
        return;
      }

      setFormData((prev) => ({
        ...prev,
        logo: files[0],
      }));
      setLogoPreview(URL.createObjectURL(files[0])); // Generate preview URL
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (name === "league") {
      fetchSeasons(value)
      fetchPlayers(value)
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessages([]);

    const data = new FormData();
    for (let [key, val] of Object.entries(formData)) {
      if (key === "logo") {
        if (val instanceof File) {
          data.append("logo", val);
        }
      } else {
        data.append(key, val);
      }
    }

    axios
      .post("/api/teams/add/", data, {
        withCredentials: true,
      })
      .then((response) => {
        onTeamAdded(response.data);
        setFormData({
          name: "",
          league: "",
          captain: "",
          logo: null,
          color: "",
        });
        setLogoPreview(null);
        handleClose();
      })
      .catch(error => {
        console.error("Error adding team:", error.response?.data);

        // Check if the response is HTML (indicating a server error)
        if (error.response?.headers?.['content-type']?.includes('text/html')) {
          setErrorMessages(["The file is too large. Please choose a file smaller than 5MB."]);
          return;
        }

        const data = error.response?.data || {};

        // Handle regular API errors
        if (typeof data === 'object') {
          const parts = Object.entries(data).flatMap(([field, msgs]) =>
            (Array.isArray(msgs) ? msgs : [msgs]).map(m =>
              `Field [${field}]: ${m}`
            )
          );
          setErrorMessages(parts);
        } else {
          setErrorMessages(["An unexpected error occurred. Please try again."]);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Team</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessages.map((err, i) => (
          <div key={i} className="text-danger">{err}</div>
        ))}
        {/* {errorMessage && <Alert variant="danger">{errorMessage}</Alert>} */}
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="name">
                <Form.Label>Team Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="league">
                <Form.Label>League</Form.Label>
                {leaguesLoading ? (
                  <p>Loading leagues...</p>
                ) : (
                  <Form.Control
                    as="select"
                    name="league"
                    value={formData.league}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a league</option>
                    {leagues.map((league) => (
                      <option key={league.id} value={league.id}>
                        {league.name}
                      </option>
                    ))}
                  </Form.Control>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="season">
                <Form.Label>Season</Form.Label>
                {seasonsLoading ? (
                  <p>Loading seasons...</p>
                ) : (
                  <Form.Control
                    as="select"
                    name="season"
                    value={formData.season}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a season</option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.name}
                      </option>
                    ))}
                  </Form.Control>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="captain">
                <Form.Label>Captain</Form.Label>
                <Form.Control
                  as="select"
                  name="captain"
                  value={formData.captain}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a captain</option>
                  {players.map((player) => (
                    <option key={player.id} value={player.id}>
                      {player.first_name} {player.last_name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="a_cap">
                <Form.Label>Assistant Captain</Form.Label>
                <Form.Control
                  as="select"
                  name="a_cap"
                  value={formData.a_cap}
                  onChange={handleChange}
                >
                  <option value="">Select an assistant captain</option>
                  {players.map((player) => (
                    <option key={player.id} value={player.id}>
                      {player.first_name} {player.last_name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="color">
                <Form.Label>Team Color</Form.Label>
                <Form.Control
                  as="select"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a color</option>
                  {colorChoices.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <div className="text-center mt-2">
                <Icon
                  path={mdiTshirtCrew}
                  size={11}
                  style={{ color: formData.color || "black" }}
                />
              </div>
            </Col>
            <Col md={6}>
              <Form.Group controlId="logo">
                <Form.Label>Logo</Form.Label>
                <Form.Control type="file" name="logo" onChange={handleChange} />
                {logoPreview && (
                  <div className="mt-3 text-center">
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      style={{
                        maxWidth: "250px",
                        maxHeight: "250px",
                        objectFit: "cover",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <div className="text-end">
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : "Add Team"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AddTeamModal;